/**
 * Key Derivation for Web Viewer
 *
 * Uses HKDF with SHA-256 to derive keys from PostKey.
 * Must match exactly with mobile app's derivation in postCrypto.ts and assetCrypto.ts
 */

import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';

/**
 * Derive MetaKey from PostKey for caption decryption
 *
 * Must match mobile app: HKDF(sha256, postKey, salt=zeros(32), info="post-meta:caption", 32)
 */
export function deriveMetaKey(postKey: Uint8Array): Uint8Array {
  if (postKey.length !== 32) {
    throw new Error('PostKey must be exactly 32 bytes');
  }

  const salt = new Uint8Array(32); // All zeros
  const info = new TextEncoder().encode('post-meta:caption');

  return hkdf(sha256, postKey, salt, info, 32);
}

/**
 * Derive MetaKey-Assets from PostKey for asset CEK unwrapping
 *
 * Must match mobile app: HKDF(sha256, postKey, salt=undefined, info="post-meta:assets", 32)
 */
export function deriveMetaKeyAssets(postKey: Uint8Array): Uint8Array {
  if (postKey.length !== 32) {
    throw new Error('PostKey must be exactly 32 bytes');
  }

  const info = new TextEncoder().encode('post-meta:assets');

  // Note: undefined salt in noble/hashes means no salt
  return hkdf(sha256, postKey, undefined, info, 32);
}

/**
 * Derive MetaKey-AssetDetails from PostKey for per-asset detail decryption
 *
 * Must match the native apps: HKDF(sha256, postKey, salt=empty, info="post-meta:asset-details", 32)
 *
 * iOS and Android both pass an *empty* salt here. Under RFC 5869 an empty salt
 * is defined as HashLen zero bytes, so the zeros(32) form used by
 * `deriveMetaKey` above and the undefined form used by `deriveMetaKeyAssets`
 * are all equivalent for SHA-256 — the three helpers only look inconsistent.
 *
 * Pinned vector (postKey = 0x00..0x1f):
 *   db7eacfce7e5708a3ae206a4d04681ab5ac92f03d03640a8409bfeb9bf302a2d
 * The same value is asserted in iOS `PostAssetDetailTests` and Android
 * `PostCryptoServiceAssetDetailTest`.
 */
export function deriveMetaKeyAssetDetails(postKey: Uint8Array): Uint8Array {
  if (postKey.length !== 32) {
    throw new Error('PostKey must be exactly 32 bytes');
  }

  const info = new TextEncoder().encode('post-meta:asset-details');

  return hkdf(sha256, postKey, undefined, info, 32);
}
